import { isAxiosError } from 'axios'

import type { AccountLockedBody } from '@/lib/api/client'

/**
 * The local-clock instant at which a locked account may try again, or null
 * when `error` is not a 403 account_locked — or is one from an older API
 * that does not say how long (the caller keeps its static message).
 *
 * A duration beats an instant: `retryAfterSeconds` (and the Retry-After
 * header, which carries the same number) survives a skewed device clock,
 * where `lockedUntil` does not. It is read last, and only while it still
 * lies ahead by our clock — counting down from a past instant would end the
 * lockout before it was ever shown.
 */
export function lockedUntilMsFrom(error: unknown, now = Date.now()): number | null {
  if (!isAxiosError(error)) return null
  const resp = error.response
  if (!resp || resp.status !== 403) return null
  const body = resp.data as Partial<AccountLockedBody> | undefined
  if (body?.code !== 'account_locked') return null

  const seconds = body.retryAfterSeconds
  if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
    return now + seconds * 1000
  }
  // Lower-cased by axios.
  const header = Number(resp.headers?.['retry-after'])
  if (Number.isFinite(header) && header > 0) return now + header * 1000

  const until = body.lockedUntil ? Date.parse(body.lockedUntil) : NaN
  return until > now ? until : null
}
